import React, {useEffect, useMemo, useState} from 'react';
import {api} from '../../api/apiService';
import {formatBackendErrors} from '../../utils/errorUtils';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  TextField,
  Typography
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PermissionsSelector from "../analytic-profiles/PermissionsSelector";
import {AnalyticProfile} from '../../types/analytic-profile';
import { EmbeddedFormSaveButton } from '../forms/EmbeddedFormSaveButton';
import { formatUnmetPasswordRules } from '../account/passwordPolicy';
import { PasswordRequirements, PasswordVisibilityToggle } from '../account/PasswordInputs';

/** Outcome shown by the parent after a successful create/update. */
export type UserFormNotice = { severity: 'success' | 'info' | 'warning'; text: string };

/** invite_sent from POST /api/users: queued | skipped | failed. */
type InviteSent = 'queued' | 'skipped' | 'failed';

export const createNotice = (email: string, inviteSent: InviteSent | undefined, hasPassword: boolean): UserFormNotice => {
  switch (inviteSent) {
    case 'queued':
      return {
        severity: 'success',
        text: hasPassword
          ? `User created. A welcome email is on its way to ${email}; share the password with them yourself.`
          : `User created. A welcome email with a link to set their password is on its way to ${email}.`,
      };
    case 'failed':
      return {
        severity: 'warning',
        text: `User created, but the welcome email to ${email} couldn't be sent. Use "Resend invite" in the users list to try again.`,
      };
    default:
      return {
        severity: 'info',
        text: hasPassword
          ? 'User created. No welcome email was sent.'
          : `User created. No welcome email was sent; ${email} can sign in after using "Forgot Password?" on the login page.`,
      };
  }
};

interface UserFormProps {
  userId?: string;
  mode: 'create' | 'edit' | 'view';
  onClose?: () => void;
  /** Called after a successful create or update (before `onClose`); create passes the invite outcome. */
  onSuccess?: (notice?: UserFormNotice) => void;
  /** When true, omit full-page container sizing for use inside a drawer. */
  embedded?: boolean;
  /** For view/edit: updates parent drawer title to "{First} {Last} - profile details". */
  onHeaderDisplayNameChange?: (title: string | null) => void;
}

const profileSearchFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    minHeight: 40,
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
};

export const UserForm: React.FC<UserFormProps> = ({
  mode,
  userId,
  onClose,
  onSuccess,
  embedded = false,
  onHeaderDisplayNameChange,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // The field is optional (empty = invite), so the rules show once the admin first focuses it,
  // then stay (tabbing on to the eye button mustn't collapse them and shift the layout).
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const [passwordError, setPasswordError] = useState('');
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [analyticProfiles, setAnalyticProfiles] = useState<AnalyticProfile[]>([]);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isNotEditable, setIsNotEditable] = useState(false);
  // Create starts from an empty table (nothing beyond the basics, which every user gets anyway);
  // edit/view wait for the user's stored permissions.
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(mode !== 'create');
  const [profilePick, setProfilePick] = useState<AnalyticProfile | null>(null);

  useEffect(() => {
    // Use /all to fetch all tenant profiles for user assignment (requires Users Update permission)
    api.get('/api/analytic-profiles/all')
      .then((res) => {
        setAnalyticProfiles(res);
      })
      .catch((res) => setError(formatBackendErrors(res)));

    if ((mode === 'edit' || mode === 'view') && userId) {
      setIsFetching(true);
      setIsNotEditable(mode === 'view');
      api.get(`/api/users/${userId}`)
        .then((response) => {
          const {first_name, last_name, username, can_manage_users, analytic_profiles, permissions} = response;
          setFirstName(first_name);
          setLastName(last_name);
          setUsername(username);
          setCanManageUsers(can_manage_users || false);
          setSelectedProfileIds(analytic_profiles || []);
          setPermissions(permissions || {});
          setIsPermissionsLoading(false)
        })
        .catch((err) => setError(formatBackendErrors(err)))
        .finally(() => setIsFetching(false));
    }
  }, [mode, userId]);

  useEffect(() => {
    if (!onHeaderDisplayNameChange) return;
    if (mode === 'create') {
      onHeaderDisplayNameChange(null);
      return;
    }
    const t = `${firstName} ${lastName}`.trim();
    onHeaderDisplayNameChange(t ? `${t} - profile details` : null);
  }, [mode, firstName, lastName, onHeaderDisplayNameChange]);

  const profileOptions = useMemo(
    () => analyticProfiles.filter((p) => !selectedProfileIds.includes(p.id)),
    [analyticProfiles, selectedProfileIds]
  );

  const addProfile = (p: AnalyticProfile | null) => {
    if (!p || isNotEditable) return;
    if (selectedProfileIds.includes(p.id)) return;
    setSelectedProfileIds((prev) => [...prev, p.id]);
    setProfilePick(null);
  };

  const removeProfile = (id: string) => {
    if (isNotEditable) return;
    setSelectedProfileIds((prev) => prev.filter((x) => x !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isCreate = mode === 'create';
    if (isCreate && password) {
      const policyMessage = formatUnmetPasswordRules(password);
      if (policyMessage) {
        setPasswordError(policyMessage);
        return;
      }
    }
    setPasswordError('');
    setIsLoading(true);

    const payload: Record<string, unknown> = {
      first_name: firstName,
      last_name: lastName,
      username,
      can_manage_users: canManageUsers,
      analytic_profiles: selectedProfileIds,
      permissions,
    };
    if (isCreate) {
      if (password) payload.password = password;
      payload.send_invite = sendInvite;
      // Origin for the links in the welcome email; the API only accepts this tenant's own host.
      payload.base_url = window.location.origin;
    }

    const apiCall = isCreate ? api.post : api.put;
    const endpoint = isCreate ? '/api/users' : `/api/users/${userId}`;

    apiCall(endpoint, payload)
      .then((res: { invite_sent?: InviteSent } | undefined) => {
        onSuccess?.(isCreate ? createNotice(username, res?.invite_sent, Boolean(password)) : undefined);
        onClose?.();
      })
      .catch((res) => setError(formatBackendErrors(res)))
      .finally(() => setIsLoading(false));
  };

  if (isFetching) {
    return <Box display="flex" justifyContent="center" mt={4}><CircularProgress sx={{ color: '#60a5fa' }}/></Box>;
  }

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fafafa',
      '& fieldset': { borderColor: '#3f3f46' },
      '&:hover fieldset': { borderColor: '#a1a1aa' },
      '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
    },
    '& .MuiInputLabel-root': { color: '#a1a1aa' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' }
  };

  const shell = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <PersonOutlineIcon sx={{ fontSize: 56, color: '#52525b' }} aria-hidden />
      </Box>
      {error && (
        <Alert 
          severity="error" 
          sx={{
            mb: 2, 
            bgcolor: '#27272a', 
            color: '#f87171', 
            border: '1px solid #3f3f46' 
          }}
        >
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          label="First Name"
          fullWidth
          margin="normal"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          disabled={isFetching || isNotEditable}
          sx={textFieldSx}
        />
        <TextField
          label="Last Name"
          fullWidth
          margin="normal"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          disabled={isFetching || isNotEditable}
          sx={textFieldSx}
        />
        <TextField
          label="Email"
          type="email"
          fullWidth
          margin="normal"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={isFetching || isNotEditable}
          sx={textFieldSx}
        />

        {mode === 'create' && (
          <>
            <TextField
              label="Password (optional)"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError('');
              }}
              error={!!passwordError}
              helperText={
                passwordError ||
                (password
                  ? 'Share this password with the user yourself.'
                  : sendInvite
                    ? 'Leave empty to email an invitation to set their own password.'
                    : 'Leave empty and the user will need "Forgot Password?" on the login page to sign in.')
              }
              FormHelperTextProps={{ sx: { color: passwordError ? undefined : '#71717a' } }}
              sx={textFieldSx}
              onFocus={() => setShowPasswordRules(true)}
              InputProps={{
                endAdornment: (
                  <PasswordVisibilityToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} label="Password" />
                ),
              }}
            />
            {(showPasswordRules || password) && <PasswordRequirements password={password} />}
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                  sx={{ color: '#71717a', '&.Mui-checked': { color: '#60a5fa' } }}
                />
              }
              label="Send welcome email"
              sx={{ color: '#d4d4d8', mt: 0.5 }}
            />
          </>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1, color: '#a1a1aa', fontWeight: 600 }}>
          Analytic profiles
        </Typography>
        {!isNotEditable ? (
          analyticProfiles.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#71717a', mb: 1 }}>
              No analytic profiles available.
            </Typography>
          ) : (
            <>
              <Autocomplete
                size="small"
                fullWidth
                options={profileOptions}
                value={profilePick}
                onChange={(_, v) => addProfile(v)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search profiles to add…"
                    sx={profileSearchFieldSx}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      bgcolor: '#111',
                      border: '1px solid #27272a',
                      '& .MuiAutocomplete-option': {
                        color: '#fafafa',
                        fontSize: '0.875rem',
                        '&:hover': { bgcolor: '#1a1a1a' },
                        '&[aria-selected="true"]': { bgcolor: '#27272a' },
                      },
                    },
                  },
                }}
              />
              <Box
                sx={{
                  mt: 1.25,
                  minHeight: 40,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                {selectedProfileIds.map((id) => {
                  const profile = analyticProfiles.find((p) => p.id === id);
                  return (
                    <Chip
                      key={id}
                      size="small"
                      label={profile?.name ?? id}
                      onDelete={() => removeProfile(id)}
                      sx={{
                        bgcolor: '#27272a',
                        color: '#e4e4e7',
                        border: '1px solid #3f3f46',
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 32 }}>
            {selectedProfileIds.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#71717a' }}>
                None assigned
              </Typography>
            ) : (
              selectedProfileIds.map((id) => {
                const profile = analyticProfiles.find((p) => p.id === id);
                return (
                  <Chip
                    key={id}
                    size="small"
                    label={profile?.name ?? id}
                    sx={{
                      bgcolor: '#27272a',
                      color: '#e4e4e7',
                      border: '1px solid #3f3f46',
                    }}
                  />
                );
              })
            )}
          </Box>
        )}

        {!isPermissionsLoading && (
          <>
            <Typography variant="h6" sx={{mt: 3, color: '#fafafa'}}>
              Permissions
            </Typography>
            <Divider sx={{mb: 2, borderColor: '#27272a'}}/>
            <PermissionsSelector
              permissions={permissions}
              setPermissions={setPermissions}
              readOnly={isNotEditable}
            />
          </>
        )}

        {!isNotEditable &&
          (embedded ? (
            <EmbeddedFormSaveButton
              loading={isLoading}
              idleTooltip={mode === 'create' ? 'Create user' : 'Save changes'}
              loadingTooltip={mode === 'create' ? 'Creating…' : 'Saving…'}
            />
          ) : (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  bgcolor: '#60a5fa',
                  color: '#0a0a0a',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  px: 2.5,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#3b82f6' },
                  '&.Mui-disabled': { bgcolor: '#27272a', color: '#52525b' },
                }}
              >
                {isLoading ? (mode === 'create' ? 'Creating…' : 'Saving…') : mode === 'create' ? 'Create user' : 'Save changes'}
              </Button>
            </Box>
          ))}
      </form>
    </>
  );

  return embedded ? (
    <Box sx={{ width: '100%', bgcolor: '#0a0a0a', p: 3 }}>{shell}</Box>
  ) : (
    <Container maxWidth="sm" sx={{ mt: 0, bgcolor: '#0a0a0a', p: 3, borderRadius: 1 }}>
      {shell}
    </Container>
  );
};
